import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, MapPin, ArrowUpRight } from "lucide-react";
import { BrandImage } from "@/lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map a member count to a readable label so the section paragraph stays human.
function teamSizeLabel(n) {
    const words = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];
    return n <= 10 ? `${words[n]}-person` : `${n}-person`;
}

const LAB_IMG =
    "https://images.unsplash.com/photo-1616996690916-a4d9af24295d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxkZW50YWwlMjBsYWIlMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc3ODQwOTM2OXww&ixlib=rb-4.1.0&q=85";
const LAB_IMG_2 =
    "https://images.pexels.com/photos/36101264/pexels-photo-36101264.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CRAFT_IMG =
    "https://images.unsplash.com/photo-1776406987595-ba14f3510c07?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxkZW50YWwlMjBsYWIlMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc3ODQwOTM2OXww&ixlib=rb-4.1.0&q=85";

export default function About() {
    // Founders + leadership fetched from DB (managed in Admin → About / Founders).
    // While the API is loading we show nothing in the People section to avoid
    // flashing placeholder cards. An empty DB collection ≡ "this lab is private".
    const [team, setTeam] = useState(null);  // null = loading, [] = empty, [...] = loaded
    useEffect(() => {
        axios.get(`${API}/team`)
            .then((r) => setTeam(Array.isArray(r.data) ? r.data : []))
            .catch(() => setTeam([]));
    }, []);
    return (
        <div data-testid="about-page" className="bg-white">
            {/* HERO */}
            <section className="border-b border-slate-200">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-8">
                        <div className="tr-overline mb-4">· About the lab</div>
                        <h1 className="font-display font-light text-5xl lg:text-7xl tracking-tight leading-[0.98] text-slate-900">
                            We retired the<br />slow dental lab.
                        </h1>
                    </div>
                    <div className="lg:col-span-4">
                        <p className="text-[15.5px] text-slate-600 leading-[1.75]">
                            Toothrocket was founded in 2021 to replace plaster, milling and a 7-day wait
                            with photopolymer chemistry, robotics, and same-day dispatch — operating from our Doha hub.
                        </p>
                    </div>
                </div>
            </section>

            {/* OUR STORY */}
            <section className="max-w-[1280px] mx-auto px-5 lg:px-8 py-24 grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <div className="rounded-3xl overflow-hidden border border-slate-200">
                        <BrandImage slug="about_lab_interior" fallback={LAB_IMG} alt="Toothrocket lab interior" className="w-full h-[480px] object-cover" />
                    </div>
                </div>
                <div className="lg:col-span-6 lg:col-start-7">
                    <div className="tr-overline mb-3">· Our story</div>
                    <h2 className="font-display font-light text-4xl lg:text-5xl tracking-tight leading-[1.05] text-slate-900">
                        From a 12 m² studio to<br />a Gulf precision dental lab.
                    </h2>
                    <div className="mt-7 space-y-5 text-[15.5px] text-slate-600 leading-[1.8]">
                        <p>
                            We started in 2021 with a single resin printer and one stubborn belief — that
                            dentists shouldn&rsquo;t have to wait a week for a crown that fits. Today, our Doha
                            facility runs around the clock, fabricating thousands of precision-printed
                            restorations every month for partner clinics across the Gulf.
                        </p>
                        <p>
                            Every Toothrocket unit ships under a 10-year clinical warranty with an auto-generated
                            digital warranty card. Not because it&rsquo;s a marketing line, but because we engineered
                            the resin chemistry, the print profile, and the QC protocol around that promise from day one.
                        </p>
                        <p>
                            We don&rsquo;t do gimmicks. No metaverse showroom. No NFT tooth. Just
                            digital dentistry done quietly, repeatably, well.
                        </p>
                    </div>
                </div>
            </section>

            {/* FACILITY TOUR */}
            <section className="bg-slate-50 border-y border-slate-200" data-testid="facility-tour">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-24">
                    <div className="grid lg:grid-cols-12 gap-10 mb-12">
                        <div className="lg:col-span-7">
                            <div className="tr-overline mb-3">· Facility tour</div>
                            <h2 className="font-display font-light text-4xl lg:text-5xl tracking-tight leading-tight text-slate-900">
                                A clean room, <span className="italic">on the record.</span>
                            </h2>
                        </div>
                        <div className="lg:col-span-4 lg:col-start-9 self-end">
                            <p className="text-slate-600 leading-relaxed text-[15px]">
                                ISO 13485 process-aligned. Air handling, temperature, and resin tank
                                contamination logged every shift.
                            </p>
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-12 gap-4">
                        <div className="lg:col-span-7 rounded-2xl overflow-hidden border border-slate-200">
                            <BrandImage slug="about_lab_2" fallback={LAB_IMG_2} alt="High-tech dental lab" className="w-full h-[460px] object-cover" />
                        </div>
                        <div className="lg:col-span-5 grid grid-rows-2 gap-4">
                            <div className="rounded-2xl overflow-hidden border border-slate-200">
                                <BrandImage slug="about_craft" fallback={CRAFT_IMG} alt="Lab technician at work" className="w-full h-[222px] object-cover" />
                            </div>
                            <div className="rounded-2xl bg-[#0B1120] text-white p-7 flex flex-col justify-between">
                                <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-cyan-400">· Print floor metrics</div>
                                <div className="space-y-3">
                                    <Stat k="32" l="High-precision printers" />
                                    <Stat k="12,400+" l="Cases printed in 2025" />
                                    <Stat k="480" l="Partner clinics" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* THE EXPERTS — only renders when at least one DB entry exists.
                Hides entirely on labs that haven't seeded their team yet, which
                is the right behaviour for white-label tenant deployments. */}
            {team && team.length > 0 && (
            <section id="team" className="max-w-[1280px] mx-auto px-5 lg:px-8 py-24" data-testid="team-section">
                <div className="grid lg:grid-cols-12 gap-10 mb-14">
                    <div className="lg:col-span-7">
                        <div className="tr-overline mb-3">· The experts</div>
                        <h2 className="font-display font-light text-4xl lg:text-5xl tracking-tight leading-tight text-slate-900">
                            The humans behind<br /><span className="italic">the print head.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-4 lg:col-start-9 self-end">
                        <p className="text-slate-600 leading-relaxed text-[15px]">
                            A {teamSizeLabel(team.length)} clinical core. Additional technicians, designers and dispatch staff. One single Slack channel for case escalations.
                        </p>
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {team.map((m) => (
                        <div key={m.id || m.name} className="tr-card p-6" data-testid={`team-card-${m.id || m.name}`}>
                            <div className="aspect-[4/5] rounded-xl mb-5 overflow-hidden bg-gradient-to-br from-slate-100 to-cyan-50 flex items-center justify-center">
                                {m.photo_data_url ? (
                                    <img src={m.photo_data_url} alt={m.name} className="w-full h-full object-cover" />
                                ) : (
                                    <span className="font-display text-6xl text-slate-300 tracking-tighter">{m.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}</span>
                                )}
                            </div>
                            <div className="font-display text-xl tracking-tight text-slate-900">{m.name}</div>
                            <div className="font-mono-tr text-[11px] tracking-[0.16em] uppercase text-cyan-700 mt-1">{m.role}</div>
                            {m.bio && <p className="mt-3 text-[13.5px] text-slate-600 leading-relaxed">{m.bio}</p>}
                        </div>
                    ))}
                </div>
            </section>
            )}

            {/* LOCATION */}
            <section className="border-t border-slate-200 bg-slate-50/60">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-24 grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <div className="tr-overline mb-3">· Visit us</div>
                        <h2 className="font-display font-light text-4xl tracking-tight text-slate-900 leading-tight">
                            Visit our Doha lab.
                        </h2>
                        <p className="mt-4 text-slate-600 max-w-xl text-[15px] leading-relaxed">
                            Drop in to see the lab in person. We host clinics for facility tours every Friday
                            afternoon — bring a difficult case, leave with a printed prototype.
                        </p>
                        <div className="mt-7 flex flex-wrap gap-3">
                            <Link to="/workflow#send-case" className="tr-btn">Send a Case <ArrowUpRight size={16} /></Link>
                            <a href="mailto:tour@toothrocket.com" className="tr-btn-ghost">Book a tour <ArrowRight size={16} /></a>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="rounded-2xl border border-slate-200 bg-[#0B1120] text-white p-7">
                            <div className="flex items-center gap-2 text-cyan-300"><MapPin size={16} /> <span className="text-[14px] font-medium">Doha · Headquarters</span></div>
                            <div className="mt-3 text-[14.5px] text-white/75 leading-relaxed">
                                Toothrocket Labs Qatar<br />
                                Building 46, Floor 1, Flat 2<br />
                                Street 989, Zone 53<br />
                                Al Shafi Street, New Al Rayyan<br />
                                Doha, Qatar
                            </div>
                            <div className="mt-4 text-[12.5px] text-white/60 font-mono-tr tracking-wide">
                                CR No · 42211
                            </div>
                            <a href="tel:+97455060499" className="mt-2 inline-flex items-center gap-1.5 text-[13.5px] text-cyan-300 hover:text-cyan-200" data-testid="doha-phone">
                                +974 5506 0499
                            </a>
                            <div className="mt-5 pt-5 border-t border-white/10 text-[13px] text-white/60">
                                Sun–Thu · 09:00 – 19:00 AST
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

function Stat({ k, l }) {
    return (
        <div>
            <div className="font-display text-3xl tracking-tight">{k}</div>
            <div className="text-[12px] text-white/55 font-mono-tr tracking-[0.16em] uppercase">{l}</div>
        </div>
    );
}
